import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { investmentFunds, stepIDs } from 'utils/constants';
import { useCheckInvestmentFund, useTunnelNavigation, useTunnelPhases } from 'utils/hooks';

import withTunnel from './withTunnel';
import TunnelPage from './common/TunnelPage';
import PropTypes from 'prop-types';

const IndirectSubscription = props => {
    const {
        allStepIds,
        allTunnelStepIds,
        codeLoader,
        isFCPR,
        label,
        lastTunnelStepMemo,
        loggedIn,
        phaseMemo,
        rolesLoader,
        roleSubmitted,
        stepId,
        stepLoader,
        submittedPedagogicalStep,
        tsId,
        tunnelStepMemo,
        tunnelSubscription,
    } = props;

    const isBexIV = useCheckInvestmentFund(investmentFunds.BEX_IV);
    const isClub1 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_1);
    const isClub2 = useCheckInvestmentFund(investmentFunds.CLUB_DEAL_2);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const internalUser = useSelector(userSelectors.getInternalUser);

    const firstCustomStepIndex = useMemo(
        () => allStepIds.findIndex(sId => sId === stepIDs.CUSTOM_STEP),
        [allStepIds]
    );

    // investor tunnel specific logic
    const navigateToStep = useTunnelNavigation(
        tsId || (!tsId && stepId === stepIDs.CUSTOM_STEP)
            ? { tsId: tsId || allTunnelStepIds[firstCustomStepIndex] }
            : undefined
    );
    const { phases, isLoading: tunnelListLoader } = useTunnelPhases();

    const dataIsLoading = useMemo(
        () => stepLoader || tunnelListLoader || rolesLoader || codeLoader,
        [stepLoader, tunnelListLoader, rolesLoader, codeLoader]
    );
    const blockingStepForIndirectSub = useMemo(() => {
        const hasInvestingSum = !!tunnelSubscription?.investmentCharacteristics?.investingSum;
        const hasPhoneNumber = !!internalUser?.phoneNumber;
        const fpciExcludedStepIds = [
            stepIDs.ACCOUNT_CREATION,
            stepIDs.KYC_PEDAGOGIC_STEP,
            stepIDs.ELIGIBILITY,
        ];
        const fcprExcludedContactInfo = [stepIDs.ACCOUNT_CREATION, stepIDs.CONTACT_INFO];
        const fcprExcludedEligibility = [...fcprExcludedContactInfo, stepIDs.ELIGIBILITY];

        if (isBexIV || isClub1 || isClub2) {
            if (!fpciExcludedStepIds.includes(tunnelStepMemo?.step?.id) && !hasInvestingSum) {
                return stepIDs.ELIGIBILITY;
            }
            if (!hasPhoneNumber) {
                return stepIDs.CONTACT_INFO;
            }
        } else if (isFCPR) {
            if (!hasPhoneNumber && !fcprExcludedContactInfo.includes(tunnelStepMemo?.step?.id)) {
                return stepIDs.CONTACT_INFO;
            } else if (
                !fcprExcludedEligibility.includes(tunnelStepMemo?.step?.id) &&
                !hasInvestingSum
            ) {
                return stepIDs.ELIGIBILITY;
            }
        }
        return null;
    }, [tunnelStepMemo, tunnelSubscription, internalUser, isFCPR, isBexIV, isClub1, isClub2]);

    useEffect(() => {
        if (dataIsLoading || !phases.length) return;

        // current tunnelStep index by stepId from the URL
        const currentStepIndex = allStepIds.findIndex(sId => sId === stepId);
        const blockingStepIndex = allStepIds.findIndex(sId => sId === blockingStepForIndirectSub);

        if (stepId === stepIDs.CUSTOM_STEP && !tsId) {
            // reset URL params for custom steps
            navigateToStep(stepId);
        } else if (
            blockingStepForIndirectSub &&
            !isCreatingSubscription &&
            currentStepIndex > blockingStepIndex
        ) {
            navigateToStep(blockingStepForIndirectSub);
        } else if (
            !loggedIn &&
            roleSubmitted &&
            phaseMemo &&
            (isFCPR ? submittedPedagogicalStep : true)
        ) {
            navigateToStep(lastTunnelStepMemo?.step.id || allStepIds[0]);
        } else if (
            roleSubmitted &&
            (isFCPR ? submittedPedagogicalStep : true) &&
            (!allStepIds.find(sId => sId === stepId) || !stepId)
        ) {
            navigateToStep(allStepIds[0]);
        }
    }, [
        dataIsLoading,
        isCreatingSubscription,
        allStepIds,
        phases,
        navigateToStep,
        stepId,
        roleSubmitted,
        phaseMemo,
        loggedIn,
        isFCPR,
        submittedPedagogicalStep,
        lastTunnelStepMemo,
        tunnelSubscription,
        label,
        blockingStepForIndirectSub,
        tsId,
    ]);

    return <TunnelPage {...props} />;
};

IndirectSubscription.propTypes = {
    allStepIds: PropTypes.array,
    allTunnelStepIds: PropTypes.array,
    codeLoader: PropTypes.bool,
    isFCPR: PropTypes.bool,
    label: PropTypes.string,
    lastTunnelStepMemo: PropTypes.object,
    loggedIn: PropTypes.bool,
    phaseMemo: PropTypes.object,
    rolesLoader: PropTypes.bool,
    roleSubmitted: PropTypes.bool,
    stepId: PropTypes.number,
    stepLoader: PropTypes.bool,
    submittedPedagogicalStep: PropTypes.bool,
    tsId: PropTypes.number,
    tunnelStepMemo: PropTypes.object,
    tunnelSubscription: PropTypes.object,
};

export default withTunnel(IndirectSubscription);
